// Resource Center / Knowledge Base content (English-only).
// Each guide is authored as structured blocks so the ResourceArticle template
// can render a consistent, high-readability layout + JSON-LD (Article + FAQPage).

export const RESOURCE_INDEX = {
  overline: "Knowledge Base",
  title: "Trade Finance Resource Center",
  intro:
    "Practical guides on UCP 600, SWIFT MT700 and ISBP 821 (2023). Guides are written from common bank discrepancy patterns.",
};

// Shared application schema injected on every resource page (helps AI + rich results).
export const SOFTWARE_APP_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "LC Shield",
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description:
    "AI-powered Letter of Credit and trade document checker. Scan SWIFT MT700 credits against invoices, bills of lading and packing lists for UCP 600 / ISBP 821 (2023) discrepancies before bank presentation.",
  url: "https://getlcshield.com",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "1 free real Letter of Credit audit, no credit card required.",
  },
  publisher: {
    "@type": "Organization",
    name: "DZent Limited",
    url: "https://getlcshield.com",
  },
};

export const GUIDES = [
  {
    slug: "top-5-ucp600-discrepancies-guide",
    category: "UCP 600",
    title: "Top 5 Bank Discrepancies Under UCP 600 & ISBP 821 (2023): How Exporters Can Eliminate Fee Penalties",
    description:
      "ICC estimates that 65-80% of first presentations are refused (ICC Technical Advisory Briefing No. 3, 2022). Learn the top 5 UCP 600 & ISBP 821 (2023) discrepancy traps — description of goods, late presentation, transport notations, non-documentary conditions and insurance — and how to prevent each.",
    readTime: "9 min read",
    updated: "Updated October 2026",
    heroTag: "Discrepancy Prevention",
    toc: [
      { id: "d1", label: "1. Description of goods mismatch" },
      { id: "d2", label: "2. Late presentation & expiry" },
      { id: "d3", label: "3. Transport document notations" },
      { id: "d4", label: "4. Non-documentary conditions" },
      { id: "d5", label: "5. Insurance cover & dates" },
      { id: "solution", label: "How pre-auditing solves this" },
    ],
    intro:
      "In international trade finance, ICC estimates that 65-80% of first presentations are refused (ICC Technical Advisory Briefing No. 3, 2022). Under UCP 600 (Uniform Customs and Practice for Documentary Credits) and ISBP 821 (2023) (International Standard Banking Practice), even minor clerical errors entitle banks to refuse payment, and bank discrepancy fees are typically USD 50-150 per refusal. Here are the top 5 most critical UCP 600 discrepancy traps faced by global exporters and how to systematically prevent them.",
    sections: [
      {
        id: "d1",
        h: "1. Description of Goods Mismatch (UCP 600 Article 18(c) · ISBP 821 (2023) — Invoices)",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Rule:", text: "Under UCP 600 Article 18(c), the description of goods, services, or performance in the Commercial Invoice must correspond directly with that in the credit." },
              { b: "Common Trap:", text: "Using generic descriptions or subtle abbreviations on the Commercial Invoice that differ from Field 45A of the SWIFT MT700. While other documents (like Packing Lists) may use general terms, the Commercial Invoice must strictly mirror the LC." },
              { b: "Prevention Standard:", text: "Ensure line-by-line exact correspondence of unit prices, item codes, and trade terms (Incoterms 2020) between MT700 Field 45A and the Commercial Invoice." },
            ],
          },
        ],
      },
      {
        id: "d2",
        h: "2. Late Presentation & Expiry Exceeded (UCP 600 Article 14(c) & Article 29)",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Rule:", text: "Under UCP 600 Article 14(c), documents comprising a transport document (e.g., Ocean Bill of Lading) must be presented by or on behalf of the beneficiary not later than 21 calendar days after the date of shipment, but in any event no later than the expiry date of the credit." },
              { b: "Common Trap:", text: "Confusing the \u201cLatest Date of Shipment\u201d (Field 44C) with the \u201cExpiry Date\u201d (Field 31D) or failing to account for bank holidays at the place of presentation." },
              { b: "Prevention Standard:", text: "Cross-check the Shipped on Board date on the Bill of Lading against Field 44C and verify that the presentation date satisfies both the 21-day rule and Field 31D." },
            ],
          },
        ],
      },
      {
        id: "d3",
        h: "3. Inconsistent Transport Document Notations (UCP 600 Article 20 · ISBP 821 (2023) — Bills of Lading)",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Rule:", text: "Under UCP 600 Article 20(a)(ii), a Bill of Lading must indicate that the goods have been shipped on board a named vessel at the port of loading stated in the credit." },
              { b: "Common Trap:", text: "A pre-printed \u201cReceived for Shipment\u201d Bill of Lading without a dated \u201cShipped on Board\u201d endorsement, or a discrepancy between the intended port of loading and the actual vessel departure port." },
              { b: "Prevention Standard:", text: "Verify that a dated \u201cShipped on Board\u201d stamp signed or initialed by the carrier or agent is explicitly present when using Bills of Lading." },
            ],
          },
        ],
      },
      {
        id: "d4",
        h: "4. Non-Documentary Conditions (UCP 600 Article 14(h))",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Rule:", text: "If a credit contains a condition without stipulating the document to indicate compliance with that condition, banks will deem such condition as not stated and will disregard it." },
              { b: "Common Trap:", text: "Exporters attempting to satisfy vague LC clauses without issuing a corresponding certificate or document, leading to confusion during document verification." },
              { b: "Prevention Standard:", text: "Identify non-documentary conditions during MT700 pre-audit and request an amendment to either remove the condition or clarify the required document." },
            ],
          },
        ],
      },
      {
        id: "d5",
        h: "5. Insurance Cover & Date Inconsistencies (UCP 600 Article 28)",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Rule:", text: "Insurance documents must be expressed in the same currency as the credit and cover at least 110% of the CIF or CIP value. Furthermore, the insurance document date must be no later than the date of shipment." },
              { b: "Common Trap:", text: "Insurance policy issued 1 day after the Bill of Lading \u201cShipped on Board\u201d date, or missing specific risk coverage clauses mandated in Field 46A." },
              { b: "Prevention Standard:", text: "Ensure policy effective dates precede or equal shipment dates and that coverage percentages precisely match UCP 600 Article 28(f)(ii)." },
            ],
          },
        ],
      },
      {
        id: "solution",
        h: "How Automated Pre-Auditing Solves Presentation Risk",
        blocks: [
          {
            type: "p",
            text: "Rather than relying on manual, high-pressure checks across dozens of pages, modern trade desks utilize specialized pre-audit platforms like LC Shield.",
          },
          {
            type: "p",
            text: "By parsing SWIFT MT700 data against UCP 600 & ISBP 821 (2023) rules typically in under 30 seconds, LC Shield automatically flags discrepancies, verifies field compliance, and generates bank-ready amendment requests before documents are submitted to issuing banks.",
          },
        ],
      },
    ],
    faq: [
      {
        q: "What percentage of Letters of Credit are rejected on first presentation?",
        a: "ICC estimates that 65-80% of first presentations are refused (ICC Technical Advisory Briefing No. 3, 2022) — most of which are avoidable clerical or consistency errors rather than fundamental trade problems.",
      },
      {
        q: "How much do bank discrepancy fees cost?",
        a: "Discrepancy fees are typically USD 50-150 per refusal under UCP 600, deducted from your proceeds, and can add days of payment delay while documents are corrected and re-presented.",
      },
      {
        q: "Does the invoice description have to match the credit exactly?",
        a: "Under UCP 600 Article 18(c), the Commercial Invoice description of goods must correspond directly with the credit (Field 45A). Other documents may use general terms as long as they do not conflict with the credit description.",
      },
      {
        q: "What are non-documentary conditions under UCP 600?",
        a: "Under UCP 600 Article 14(h), if a credit states a condition without stipulating a document to evidence compliance, banks disregard that condition as not stated. Identify these during MT700 pre-audit and request an amendment.",
      },
    ],
  },

  {
    slug: "swift-mt700-field-by-field-audit-guide",
    category: "SWIFT MT700",
    title: "SWIFT MT700 Field-by-Field Audit Guide: Essential Compliance Checkpoints for Exporters",
    description:
      "An expert field-by-field SWIFT MT700 audit checklist — 31D expiry, 44C latest shipment, 45A goods description, 46A/47A documents and conditions, and 48 presentation period — with UCP 600 / ISBP 821 (2023) rules and red flags.",
    readTime: "10 min read",
    updated: "Updated October 2026",
    heroTag: "SWIFT Decoding",
    toc: [
      { id: "f31d", label: "Field 31D — Expiry" },
      { id: "f44", label: "Field 44C vs 44D — Shipment" },
      { id: "f45a", label: "Field 45A — Goods" },
      { id: "f46a", label: "Field 46A & 47A — Documents" },
      { id: "f48", label: "Field 48 — Presentation" },
      { id: "solution", label: "Streamlining verification" },
    ],
    intro:
      "The SWIFT MT700 message format is the global standard used by issuing banks to transmit irrevocable Letters of Credit. For export document managers, performing a meticulous field-by-field audit upon receipt of an MT700 is the critical first line of defense against bank discrepancies under UCP 600. Below is an expert-level compliance audit checklist covering the key SWIFT MT700 fields, common formatting traps, and international banking rules.",
    sections: [
      {
        id: "f31d",
        h: "1. Field 31D: Date and Place of Expiry",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "Compliance Standard:", text: "Dictates the final calendar date and geographical location where documents must be presented." },
              { b: "Audit Rule:", text: "Under UCP 600 Article 6(d)(i), the credit must state an expiry date for presentation. Under art. 6(d)(ii), the place of the bank with which the credit is available is the place for presentation." },
            ],
          },
          {
            type: "callout",
            tone: "warn",
            title: "Red Flag",
            items: [
              { text: "An expiry location situated strictly at the issuing bank's counters in a foreign country, leaving the beneficiary insufficient courier transport time after shipping." },
            ],
          },
        ],
      },
      {
        id: "f44",
        h: "2. Field 44C: Latest Date of Shipment vs. Field 44D: Shipment Period",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "Compliance Standard:", text: "Specifies the final deadline for goods to be loaded on board or dispatched." },
              { b: "Audit Rule:", text: "Under UCP 600 Article 29(a), if the expiry date or last day for presentation falls on a day the bank is closed, it is extended to the next banking day; however, under Article 29(c) the latest shipment date (Field 44C) is NEVER extended." },
            ],
          },
          {
            type: "callout",
            tone: "warn",
            title: "Red Flag",
            items: [
              { text: "Confusing shipment extension rules with expiry extension rules, leading to late shipment discrepancies." },
            ],
          },
        ],
      },
      {
        id: "f45a",
        h: "3. Field 45A: Description of Goods, Services or Performance",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "Compliance Standard:", text: "The master contract baseline for invoices and trade documents." },
              { b: "Audit Rule:", text: "Under UCP 600 Article 18(c), the Commercial Invoice must strictly correspond with the exact text in Field 45A. Abbreviations, altered trade terms (e.g., FOB vs. CIF), or missing model numbers trigger automatic rejection by issuing banks." },
            ],
          },
          {
            type: "callout",
            tone: "warn",
            title: "Red Flag",
            items: [
              { text: "Overly complex technical specifications pasted into Field 45A that cannot be mirrored word-for-word on the invoice." },
            ],
          },
        ],
      },
      {
        id: "f46a",
        h: "4. Field 46A: Documents Required & Field 47A: Additional Conditions",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "Compliance Standard:", text: "Outlines every certificate, transport document, and policy needed for payment, alongside mandatory special instructions." },
              { b: "Audit Rule:", text: "Under ISBP 821 (2023) (Section A), documents must be issued by the entity named in the LC (or beneficiary if silent). Any condition in Field 47A that specifies a requirement without mandating a document (Non-Documentary Condition) is disregarded by banks under UCP 600 Article 14(h)." },
            ],
          },
          {
            type: "callout",
            tone: "warn",
            title: "Red Flag",
            items: [
              { text: "Clauses requiring \u201cApproval by Applicant's QC Inspector\u201d without specifying an Inspection Certificate document format." },
            ],
          },
        ],
      },
      {
        id: "f48",
        h: "5. Field 48: Period for Presentation",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "Compliance Standard:", text: "Number of days allowed after shipment for document submission." },
              { b: "Audit Rule:", text: "Under UCP 600 Article 14(c), if Field 48 is omitted, the default legal presentation period is 21 calendar days after the date of shipment (but not later than the expiry date in Field 31D)." },
            ],
          },
          {
            type: "callout",
            tone: "warn",
            title: "Red Flag",
            items: [
              { text: "Setting Field 48 to an unrealistically short timeframe (e.g., 5 days) when obtaining Bills of Lading or Certificate of Origin takes longer." },
            ],
          },
        ],
      },
      {
        id: "solution",
        h: "Streamlining MT700 Verification with Automated Pre-Audits",
        blocks: [
          {
            type: "p",
            text: "Manually reviewing SWIFT MT700 tags against dozens of ISBP 821 (2023) clauses is time-consuming and prone to human oversight under tight shipping deadlines.",
          },
          {
            type: "p",
            text: "LC Shield automatically maps SWIFT MT700 text fields, validates field logic against UCP 600 / ISBP 821 (2023) standards, and highlights discrepancy traps typically within 30 seconds. Exporters can pre-screen live MT700 data and draft immediate amendment requests before commencing shipment.",
          },
        ],
      },
    ],
    faq: [
      {
        q: "What is a SWIFT MT700 message?",
        a: "The MT700 is the standardised SWIFT message format issuing banks use to transmit an irrevocable documentary Letter of Credit. Its numbered fields encode every requirement the beneficiary must satisfy to be paid.",
      },
      {
        q: "Is the latest shipment date extended for bank holidays?",
        a: "No. Under UCP 600 Article 29(a) the expiry date (Field 31D) is extended to the next banking day if it falls on a day the bank is closed, but under Article 29(c) the latest shipment date (Field 44C) is never extended.",
      },
      {
        q: "What happens if Field 48 (period for presentation) is omitted?",
        a: "Under UCP 600 Article 14(c), if Field 48 is not stated the default legal presentation period is 21 calendar days after the date of shipment, and never later than the expiry date in Field 31D.",
      },
      {
        q: "What is field 47A on an MT700?",
        a: "Field 47A holds Additional Conditions. Any condition that specifies a requirement without mandating a supporting document is a non-documentary condition and is disregarded by banks under UCP 600 Article 14(h).",
      },
    ],
  },

  {
    slug: "isbp821-bill-of-lading-checklist",
    category: "ISBP 821 (2023)",
    title: "Ocean Bill of Lading Audit Under UCP 600 Article 20 & ISBP 821 (2023): Avoid Rejection Traps",
    description:
      "The essential ocean Bill of Lading compliance checklist under UCP 600 Article 20 and ISBP 821 (2023) (Bills of Lading): carrier signing standards, dated shipped-on-board notations, port consistency and clean transport documents.",
    readTime: "8 min read",
    updated: "Updated October 2026",
    heroTag: "Transport Documents",
    toc: [
      { id: "carrier", label: "1. Carrier identification & signing" },
      { id: "onboard", label: "2. Dated shipped-on-board" },
      { id: "ports", label: "3. Port of loading & discharge" },
      { id: "clean", label: "4. Clean transport document" },
      { id: "solution", label: "Automate cross-checking" },
    ],
    intro:
      "Ocean Bills of Lading (B/L) are among the most heavily scrutinized documents during Letter of Credit presentations. UCP 600 Article 20 sets stringent criteria that an ocean transport document must meet to be accepted by examining banks. Here is the essential checklist for exporters to ensure full compliance under ISBP 821 (2023) Paragraphs E1–E28.",
    sections: [
      {
        id: "carrier",
        h: "1. Carrier Identification & Signing Standards (UCP 600 Article 20(a)(i))",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Requirement:", text: "The Bill of Lading must indicate the name of the carrier and be signed by the carrier, the master, or a named agent acting for or on behalf of the carrier/master." },
              { b: "Common Rejection:", text: "Signed merely as \u201cAgent\u201d without explicitly stating whether they are acting for the carrier or for the master." },
            ],
          },
        ],
      },
      {
        id: "onboard",
        h: "2. Dated \u201cShipped on Board\u201d Endorsement (UCP 600 Article 20(a)(ii))",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Requirement:", text: "If the B/L contains a pre-printed \u201cReceived for Shipment\u201d clause, it must feature an explicit, dated \u201cShipped on Board\u201d notation." },
              { b: "Common Rejection:", text: "The \u201cShipped on Board\u201d stamp is present, but lacks a date or initial/signature by the issuing party. Under UCP 600 Article 20(a)(ii), the date of the \u201cShipped on Board\u201d notation is deemed to be the official date of shipment." },
            ],
          },
        ],
      },
      {
        id: "ports",
        h: "3. Port of Loading & Port of Discharge Consistency (UCP 600 Article 20(a)(iii))",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Requirement:", text: "Goods must be shipped from the Port of Loading to the Port of Discharge specified in the credit." },
              { b: "Common Rejection:", text: "If the credit specifies \u201cHong Kong Port\u201d as the Port of Loading, but the B/L states \u201cShenzhen\u201d under Port of Loading and \u201cHong Kong\u201d under Place of Receipt, this constitutes a discrepancy unless multimodal transport (Article 19) is explicitly permitted." },
            ],
          },
        ],
      },
      {
        id: "clean",
        h: "4. Clean Transport Document (UCP 600 Article 27)",
        blocks: [
          {
            type: "ul",
            items: [
              { b: "The Requirement:", text: "A bank will only accept a clean transport document \u2014 one bearing no clause or notation expressly declaring a defective condition of the goods or packaging." },
              { b: "Common Rejection:", text: "Carrier notations such as \u201cbags torn,\u201d \u201crust-stained drums,\u201d or \u201cused packaging.\u201d Words like \u201cclean\u201d do not need to appear on the B/L, but negative clauses must be absent." },
            ],
          },
        ],
      },
      {
        id: "solution",
        h: "Automate Your Document Cross-Checking",
        blocks: [
          {
            type: "p",
            text: "Cross-referencing Bills of Lading against SWIFT MT700 Field 44A/44B/44C and Commercial Invoices requires absolute precision.",
          },
          {
            type: "p",
            text: "With LC Shield, trade desks can instantly cross-audit MT700 parameters against transport document rules, automatically identifying inconsistencies in shipment dates, ports, and carrier requirements before bank presentation.",
          },
        ],
      },
    ],
    faq: [
      {
        q: "Who must sign an ocean bill of lading under UCP 600?",
        a: "Under UCP 600 Article 20(a)(i) the B/L must name the carrier and be signed by the carrier, the master, or a named agent acting for or on behalf of the carrier or master — and an agent's signature must state whose behalf it acts on.",
      },
      {
        q: "Does a bill of lading have to say the word 'clean'?",
        a: "No. Under UCP 600 Article 27 a transport document is clean as long as it bears no clause or notation expressly declaring a defective condition of the goods or packaging. The word 'clean' does not need to appear.",
      },
      {
        q: "What date counts as the shipment date on a bill of lading?",
        a: "Under UCP 600 art. 20(a)(ii), when a dated 'Shipped on Board' notation is required, the date of that notation is the date of shipment used to test the latest shipment date (Field 44C).",
      },
      {
        q: "Is a port of loading vs place of receipt mismatch a discrepancy?",
        a: "Yes. If the credit names a specific Port of Loading but the B/L shows a different Port of Loading with the credit's port only under Place of Receipt, it is a discrepancy unless multimodal transport under UCP 600 Article 19 is explicitly permitted.",
      },
    ],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
